import axios from "axios";
import { useCallback, useState } from "react";
import { getCookieValue } from "src/constants/functions";
import { vitecompanyserviceurl } from "src/constants/global-const";
import { useToast } from "src/hooks/useToast";

interface EventCheckOptions {
  retries?: number;
  retryDelay?: number;
  setPending?: (val: boolean) => void;
  setBlocked?: (val: boolean) => void;
}

export const useEventStatus = (options?: EventCheckOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const { toast } = useToast();

  const withEventCheck = useCallback(
    <T>(operation: () => T | Promise<T>, streamId?: string): Promise<T> => {
      const { retries = 10, retryDelay = 1000 } = options || {};
      setLoading(true);
      setError(null);

      const checkEventStatus = async (): Promise<T> => {
        if (!streamId) {
          return Promise.resolve(operation());
        }

        const eventStatus = await axios.get(`${vitecompanyserviceurl}/api/v1/check-event-status?streamId=${streamId}&consumerName=V2`, {
          headers: {
            Authorization: JSON.stringify({
              authorization: getCookieValue("idToken"),
              refreshToken: getCookieValue("refreshToken"),
            }),
          },
        });

        if (eventStatus?.data?.isEventBlocked) {
          setIsBlocked(true);
          options?.setBlocked?.(true);
          toast({ description: "Event is blocked. Please try again later", variant: "destructive" });
          throw new Error("Event is blocked. Please try again later");
        }

        if (eventStatus?.data?.isEventPending) {
          let retryCount = 0;
          setIsPending(true);
          options?.setPending?.(true);

          const retry = async (): Promise<T> => {
            const status = await axios.get(`${vitecompanyserviceurl}/api/v1/check-event-status?streamId=${streamId}&consumerName=V2`, {
              headers: {
                Authorization: JSON.stringify({
                  authorization: getCookieValue("idToken"),
                  refreshToken: getCookieValue("refreshToken"),
                }),
              },
            });

            if (status?.data?.isEventPending && retryCount < retries) {
              retryCount++;
              await new Promise((resolve) => setTimeout(resolve, retryDelay));
              return retry();
            }

            if (status?.data?.isEventBlocked) {
              setIsBlocked(true);
              options?.setBlocked?.(true);
              toast({ description: "Event became blocked during retry", variant: "destructive" });
              throw new Error("Event became blocked during retry");
            }

            return Promise.resolve(operation());
          };

          return retry();
        }

        setIsPending(false);
        options?.setPending?.(false);
        return Promise.resolve(operation());
      };

      return checkEventStatus()
        .catch((err) => {
          const error = err instanceof Error ? err : new Error("Unknown error occurred");
          setError(error);
          throw error;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [options]
  );

  return {
    withEventCheck,
    loading,
    error,
    isBlocked,
    isPending,
  };
};

